import { ArrowLeftRight, AudioLines, MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { formatSimilarity } from '@/components/humint/format'
import { ShortVoiceEvidence } from '@/components/humint/voice/ShortVoiceEvidence'
import { VoiceWhen } from '@/components/identities/voices/voice-facts'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { documentPath } from '@/features/documents/document-path'
import { recordingPath } from '@/features/humint/recording/recording-path'
import type { IdentityAudioRecord as AudioRecord } from '@/services/api/identity-audio'
import { AudioPlayback } from './AudioPlayback'

const COPY = 'identity.detail.audio'

function RecordingMatches({ row }: { row: AudioRecord }) {
  const { t, i18n } = useTranslation()
  return (
    <div className="space-y-2">
      {row.matches.map(match => (
        <p key={match.match_id} className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="secondary">{t(`${COPY}.match.${match.status}`)}</Badge>
          <span>{t(`${COPY}.match.voice`, { voice: match.voice })}</span>
          {match.short_evidence && <ShortVoiceEvidence />}
          <span className="text-muted-foreground">
            {t('humint.voice.lead.similarity')} {formatSimilarity(match.similarity, i18n.language)}
            {match.provisional && ` · ${t('humint.common.bands.provisional')}`}
          </span>
        </p>
      ))}
    </div>
  )
}

function RecordingMenu({
  row,
  source,
  onCompare,
}: {
  row: AudioRecord
  source: string
  onCompare?: (matchId: string) => void
}) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t(`${COPY}.recordingActions`)}>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onCompare &&
          row.matches.map(match => (
            <DropdownMenuItem key={match.match_id} onSelect={() => onCompare(match.match_id)}>
              <ArrowLeftRight className="size-4" />
              {t(`${COPY}.match.compare`, { voice: match.voice })}
            </DropdownMenuItem>
          ))}
        <DropdownMenuItem asChild>
          <Link to={source}>{t(`${COPY}.openRecording`)}</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RecordingDetails({ row, source }: { row: AudioRecord; source: string }) {
  const { t } = useTranslation()
  return (
    <>
      {(row.voices.length > 0 || row.matches.length === 0) && (
        <p className="text-sm text-muted-foreground">
          {row.voices.length
            ? t(`${COPY}.voices`, { voices: row.voices.join(', ') })
            : t(`${COPY}.participant`)}
        </p>
      )}
      <RecordingMatches row={row} />
      <Button variant="link" size="sm" className="h-auto p-0" asChild>
        <Link to={source}>{t(`${COPY}.openRecording`)}</Link>
      </Button>
    </>
  )
}

/** One recording the person speaks in: the whole file, with its detections and its menu. */
export function IdentityAudioRecord({
  row,
  caseId,
  onCompare,
}: {
  row: AudioRecord
  caseId: string
  onCompare?: (matchId: string) => void
}) {
  const source = row.capture_id
    ? recordingPath(caseId, row.capture_id)
    : documentPath(caseId, row.document_id)
  return (
    <article className="grid gap-4 py-5 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,20rem)]">
      <div className="flex min-w-0 gap-3">
        <AudioLines aria-hidden className="mt-1 size-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-2">
          <h3 className="break-words font-medium">{row.title}</h3>
          <VoiceWhen iso={row.recorded_at} />
          <RecordingDetails row={row} source={source} />
        </div>
      </div>
      <div className="flex items-start gap-2 sm:justify-end">
        <AudioPlayback url={row.audio_url} />
        <RecordingMenu row={row} source={source} onCompare={onCompare} />
      </div>
    </article>
  )
}
